import { ThemeEvents } from '@theme/events';

const WIDGET_TYPES = ['ratings', 'reviews', 'add-review'];
// Excludes the "service" (company-wide) rating widget — see
// blocks/_voyado-service-rating.liquid — which is pinned to Voyado's reserved
// "service" identifier and must never be touched by per-variant updates.
const CONTAINER_SELECTOR = '.voyado-ratings:not([data-product-id="service"]), .voyado-reviews, .voyado-add-review';

// Only the PDP inline ratings widget (blocks/_voyado-ratings.liquid) carries
// this class — the service rating widget deliberately doesn't, so clicking it
// (e.g. in the header USP bar) never scrolls an unrelated page to a reviews
// section that may not even exist there.
const RATINGS_SCROLL_TRIGGER_SELECTOR = '.voyado-ratings--scroll-trigger';
const REVIEWS_SECTION_SELECTOR = '#voyado-reviews-section';

// Hiding while empty (no rating/reviews flash on load) is handled entirely by
// the `:empty` CSS in voyado-reviews-styles.liquid and
// sections/voyado-reviews.liquid. No JS-driven visibility timing needed here.

function loadVoyadoReviewWidgets() {
  window.voyado?.('load-widgets', WIDGET_TYPES);
}

function currentDesignCode() {
  return document.querySelector(CONTAINER_SELECTOR)?.dataset.productGroupId;
}

// Reviews are design-level (group id = design code, e.g. "91500", shared by
// all colors and sizes — see blocks/_voyado-ratings.liquid), so a size change
// only keeps variant-id in sync — as Voyado requires — without reloading the
// unchanged widget data.
function updateContainerVariant(sku) {
  document.querySelectorAll(CONTAINER_SELECTOR).forEach((container) => {
    container.dataset.variantId = sku;
  });
}

function scrollToReviewsSection() {
  const reviewsSection = document.querySelector(REVIEWS_SECTION_SELECTOR);
  if (!reviewsSection) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  reviewsSection.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  // gtm-valantic.js listens for this and pushes {event: 'review_click'}.
  document.dispatchEvent(new CustomEvent('theme:review_click'));
}

// Guards against this module's top-level side effects (listener registration,
// load-widgets) running more than once on the same page — observed in
// unpublished-theme preview (Shopify's preview-bar re-fetches/re-imports this
// file a few seconds after the initial load), which otherwise double-registers
// the listeners below and fires review_click / variant-update handling twice.
if (!window.__voyadoReviewsInitialized) {
  window.__voyadoReviewsInitialized = true;

  document.addEventListener('click', (event) => {
    if (event.target.closest(RATINGS_SCROLL_TRIGGER_SELECTOR)) scrollToReviewsSection();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!event.target.closest(RATINGS_SCROLL_TRIGGER_SELECTOR)) return;

    event.preventDefault();
    scrollToReviewsSection();
  });

  loadVoyadoReviewWidgets();
  let loadedDesignCode = currentDesignCode();

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    const sku = event.detail?.resource?.sku;
    if (sku) updateContainerVariant(sku);

    // A combined-listing swatch morphs <main> to another product, replacing
    // the containers with empty ones. Another color of the same design
    // shares its reviews, so only a different design needs a reload.
    const designCode = currentDesignCode();
    if (designCode === loadedDesignCode) return;

    loadedDesignCode = designCode;
    window.voyado?.('reset-widgets');
    loadVoyadoReviewWidgets();
  });
}
